import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Trophy, Calendar, Cake, Gift, Copy, Share2, Crown, Clock } from "lucide-react";
import { useAuth, authHeaders } from "../AuthContext";
import { useLang } from "../LanguageContext";
import BirthdayBanner from "../components/BirthdayBanner";
import EditableText from "../components/EditableText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  pending: "text-muted-foreground",
  confirmed: "text-primary",
  declined: "text-destructive",
  completed: "text-accent",
};

export default function Account() {
  const { t, lang } = useLang();
  const { user, token, logout, updateProfile } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [dob, setDob] = useState("");
  const [dobSaved, setDobSaved] = useState(false);
  const [referral, setReferral] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [membership, setMembership] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [offers, setOffers] = useState([]);
  const [mReqError, setMReqError] = useState("");

  useEffect(() => {
    if (user?.date_of_birth) setDob(user.date_of_birth);
  }, [user]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/bookings/mine`, { headers: authHeaders(token) })
      .then((r) => setBookings(r.data.bookings || []))
      .catch(() => {});
    axios.get(`${API}/referral/me`, { headers: authHeaders(token) })
      .then((r) => setReferral(r.data))
      .catch(() => {});
    axios.get(`${API}/membership/me`, { headers: authHeaders(token) })
      .then((r) => {
        setMembership(r.data);
        if (r.data?.status === "active") {
          axios.get(`${API}/membership/offers`, { headers: authHeaders(token) })
            .then((o) => setOffers(o.data.offers || []))
            .catch(() => {});
        }
      })
      .catch(() => {});
    axios.get(`${API}/waitlist/mine`, { headers: authHeaders(token) })
      .then((r) => setWaitlist(r.data.entries || []))
      .catch(() => {});
  }, [token]);

  const requestMembership = async () => {
    setMReqError("");
    try {
      await axios.post(`${API}/membership/request`, {}, { headers: authHeaders(token) });
      setMembership((m) => ({ ...m, status: "requested" }));
    } catch {
      setMReqError(lang === "fr" ? "Impossible d'envoyer la demande." : "Could not send the request.");
    }
  };

  if (!user) return null;

  const visits = user.loyalty_visits || 0;
  const progress = Math.min(visits, 10);
  const remaining = Math.max(0, 10 - visits);

  const saveDob = async () => {
    if (!dob) return;
    try {
      await updateProfile({ date_of_birth: dob });
      setDobSaved(true);
      setTimeout(() => setDobSaved(false), 2500);
    } catch {}
  };

  return (
    <main data-testid="account-page" className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 pb-24">
      {/* Birthday banner — full width if active */}
      {user.birthday?.is_birthday_month && user.birthday?.code && (
        <div className="mb-12">
          <BirthdayBanner />
        </div>
      )}

      {/* Wait-list spot opened banner */}
      {waitlist.some((w) => w.status === "notified") && (
        <div data-testid="waitlist-notified-banner" className="mb-12 border border-primary bg-primary/10 p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Clock size={20} strokeWidth={1.2} className="text-primary mt-1" />
            <div>
              <p className="qw-overline text-primary mb-1">
                {lang === "fr" ? "Une place s'est libérée !" : "A spot opened up!"}
              </p>
              <p className="text-sm text-foreground/85">
                {lang === "fr"
                  ? `Bonne nouvelle — une place est disponible le ${waitlist.find((w) => w.status === "notified")?.date}. Réservez vite.`
                  : `Good news — a spot is available on ${waitlist.find((w) => w.status === "notified")?.date}. Book it before it's gone.`}
              </p>
            </div>
          </div>
          <Link to="/booking" data-testid="waitlist-book-now-btn" className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]">
            {t.nav.bookNow} <ArrowUpRight size={12} strokeWidth={1.2} />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="qw-overline mb-4">{t.account.title}</p>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-2">{user.name}</h1>
          <p className="text-sm text-muted-foreground mb-10">{user.email}</p>

          {/* Loyalty */}
          <div data-testid="loyalty-card" className="border border-primary/40 p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Trophy size={18} strokeWidth={1.2} className="text-primary" />
              <p className="qw-overline">{t.account.loyalty}</p>
            </div>
            <p className="font-serif text-5xl text-primary qw-num mb-2">{visits}</p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mb-5">{t.account.visitCount}</p>
            <div className="h-px w-full bg-border relative mb-3">
              <div className="absolute top-0 left-0 h-full bg-primary transition-all duration-700" style={{ width: `${progress * 10}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">{remaining} {t.account.progress}</p>
            <p className="qw-overline mt-4 text-accent">{t.account.reward}</p>
          </div>

          {/* Inner Circle membership */}
          {membership && (
            <div data-testid="membership-tile" className={`border p-6 mb-8 ${membership.status === "active" ? "border-primary bg-primary/10" : "border-primary/40 bg-primary/5"}`}>
              <div className="flex items-center gap-3 mb-4">
                <Crown size={18} strokeWidth={1.2} className="text-primary" />
                <p className="qw-overline">{lang === "fr" ? "Queensway Inner Circle" : "Queensway Inner Circle"}</p>
              </div>

              {membership.status === "active" ? (
                <div data-testid="membership-active">
                  <p className="font-serif text-2xl text-primary mb-2">{lang === "fr" ? "Membre" : "Member"}</p>
                  <ul className="text-xs text-foreground/85 space-y-2 mb-4">
                    <li>· {lang === "fr" ? `−${membership.discount_pct}% sur tous les soins` : `−${membership.discount_pct}% on all treatments`}</li>
                    <li>· {lang === "fr" ? "Réservation prioritaire & liste d'attente prioritaire" : "Priority booking & wait-list priority"}</li>
                    <li>· {lang === "fr" ? "Confirmation en priorité" : "Early confirmation"}</li>
                  </ul>
                  {membership.expires && (
                    <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground">
                      {lang === "fr" ? "Valide jusqu'au" : "Valid until"} {membership.expires}
                    </p>
                  )}
                </div>
              ) : membership.status === "requested" ? (
                <div data-testid="membership-requested">
                  <p className="text-sm text-foreground/85 leading-relaxed mb-3">
                    <EditableText kEn="account_circle_requested_en" kFr="account_circle_requested_fr" label="Membership requested text"
                      fallback={{
                        en: "Request received. Settle the fee via Orange Money to activate — we'll confirm as soon as payment arrives.",
                        fr: "Demande reçue. Réglez la cotisation via Orange Money pour activer votre adhésion — nous confirmerons dès réception.",
                      }} />
                  </p>
                  <p className="font-serif text-xl text-primary qw-num">{(membership.fee_fcfa || 0).toLocaleString("fr-FR")} FCFA / {lang === "fr" ? "an" : "year"}</p>
                </div>
              ) : (
                <div data-testid="membership-pitch">
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                    <EditableText kEn="account_circle_pitch_en" kFr="account_circle_pitch_fr" label="Membership pitch"
                      fallback={{
                        en: `Join the circle: −${membership.discount_pct}% on every treatment, priority booking and first access.`,
                        fr: `Rejoignez le cercle : −${membership.discount_pct}% sur tous les soins, réservation prioritaire et accès privilégié.`,
                      }} />
                  </p>
                  <p className="font-serif text-xl text-primary qw-num mb-4">{(membership.fee_fcfa || 0).toLocaleString("fr-FR")} FCFA / {lang === "fr" ? "an" : "year"}</p>
                  <button onClick={requestMembership} data-testid="membership-request-btn"
                    className="qw-btn qw-btn-primary w-full justify-center !text-[0.7rem] !py-2.5">
                    {lang === "fr" ? "Demander à rejoindre" : "Request to join"}
                  </button>
                  {membership.status === "expired" && (
                    <p className="text-[0.65rem] text-muted-foreground mt-3">
                      {lang === "fr" ? "Votre adhésion a expiré — renouvelez-la." : "Your membership expired — renew it."}
                    </p>
                  )}
                  {mReqError && <p className="text-xs text-destructive mt-2">{mReqError}</p>}
                </div>
              )}
            </div>
          )}

          {/* Wait-list entries */}
          {waitlist.filter((w) => w.status === "waiting").length > 0 && (
            <div data-testid="waitlist-tile" className="border border-border p-6 mb-8">
              <div className="flex items-center gap-3 mb-3">
                <Clock size={16} strokeWidth={1.2} className="text-primary" />
                <p className="qw-overline">{lang === "fr" ? "Liste d'attente" : "Wait-list"}</p>
              </div>
              <ul className="text-xs text-foreground/85 space-y-2">
                {waitlist.filter((w) => w.status === "waiting").map((w) => (
                  <li key={w.id} className="flex justify-between gap-3">
                    <span>{w.date}{w.service_name ? ` · ${w.service_name}` : ""}</span>
                    <span className="text-muted-foreground uppercase tracking-wider text-[0.6rem]">{lang === "fr" ? "en attente" : "waiting"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Referral */}
          {referral?.code && (
            <div data-testid="referral-tile" className="border border-primary/40 p-6 mb-8 bg-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <Gift size={18} strokeWidth={1.2} className="text-primary" />
                <p className="qw-overline">{lang === "fr" ? "Parrainage" : "Refer a friend"}</p>
              </div>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                {lang === "fr"
                  ? "Partagez votre code. Vous recevez 5 000 FCFA — votre amie aussi — après son premier rendez-vous."
                  : "Share your code. You both get 5,000 FCFA off after her first visit."}
              </p>
              <div className="flex items-center justify-between border border-border bg-background px-4 py-3 mb-4">
                <p data-testid="referral-code" className="font-serif text-xl text-primary qw-num tracking-widest">{referral.code}</p>
                <button
                  onClick={async () => {
                    try {
                      const url = `${window.location.origin}/booking?ref=${referral.code}`;
                      await navigator.clipboard.writeText(url);
                      setRefCopied(true);
                      setTimeout(() => setRefCopied(false), 2200);
                    } catch {}
                  }}
                  data-testid="referral-copy-link-btn"
                  className="text-[0.65rem] tracking-wider uppercase text-foreground/70 hover:text-primary flex items-center gap-1"
                  aria-label="Copy referral link"
                >
                  <Copy size={11} strokeWidth={1.2} />
                  {refCopied ? (lang === "fr" ? "Copié" : "Copied") : (lang === "fr" ? "Copier" : "Copy")}
                </button>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  (lang === "fr"
                    ? `J'adore Queensway Studio à Dakar. Utilise mon code ${referral.code} et reçois 5 000 FCFA sur ton premier soin : `
                    : `I love Queensway Studio in Dakar. Use my code ${referral.code} and get 5,000 FCFA off your first treatment: `)
                  + `${window.location.origin}/booking?ref=${referral.code}`
                )}`}
                target="_blank" rel="noreferrer"
                data-testid="referral-whatsapp-share-btn"
                className="qw-btn qw-btn-outline w-full justify-center !text-[0.7rem] !py-2.5"
              >
                <Share2 size={11} strokeWidth={1.2} />
                {lang === "fr" ? "Partager via WhatsApp" : "Share via WhatsApp"}
              </a>
              <div className="grid grid-cols-2 gap-px bg-border mt-5 border border-border">
                <div className="bg-background p-3 text-center">
                  <p className="text-[0.6rem] tracking-wider uppercase text-muted-foreground">{lang === "fr" ? "Crédit" : "Credit"}</p>
                  <p data-testid="referral-credit" className="font-serif text-lg text-primary qw-num mt-1">{(referral.credit_fcfa || 0).toLocaleString("fr-FR")} FCFA</p>
                </div>
                <div className="bg-background p-3 text-center">
                  <p className="text-[0.6rem] tracking-wider uppercase text-muted-foreground">{lang === "fr" ? "Amies parrainées" : "Friends referred"}</p>
                  <p data-testid="referral-count" className="font-serif text-lg text-primary qw-num mt-1">{referral.successful_referrals || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Birthday DOB editor (when not set) */}
          {!user.date_of_birth && (
            <div data-testid="dob-prompt" className="border border-primary/40 p-6 mb-8 bg-primary/5">
              <div className="flex items-center gap-3 mb-3">
                <Cake size={18} strokeWidth={1.2} className="text-primary" />
                <p className="qw-overline">{t.birthday.addDob}</p>
              </div>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{t.auth.dobHint}</p>
              <input
                type="date"
                data-testid="account-dob-input"
                value={dob}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setDob(e.target.value)}
                className="qw-input mb-3"
              />
              <button
                onClick={saveDob}
                data-testid="account-dob-save-btn"
                disabled={!dob}
                className="qw-btn qw-btn-outline w-full justify-center disabled:opacity-40 !text-[0.7rem] !py-2.5"
              >
                {dobSaved ? t.birthday.dobSaved : t.auth.dob.replace(" (optional)", "").replace(" (optionnel)", "")}
              </button>
            </div>
          )}

          {user.date_of_birth && !user.birthday?.is_birthday_month && (
            <div data-testid="dob-saved" className="border border-border p-5 mb-8 flex items-center gap-3">
              <Cake size={16} strokeWidth={1.2} className="text-primary" />
              <div>
                <p className="qw-overline text-muted-foreground">{t.auth.dob.replace(" (optional)", "").replace(" (optionnel)", "")}</p>
                <p className="text-sm text-foreground/85">{new Date(user.date_of_birth).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long" })}</p>
              </div>
            </div>
          )}

          <button
            data-testid="account-logout-btn"
            onClick={logout}
            className="qw-btn qw-btn-outline w-full justify-center"
          >
            {t.auth.logout}
          </button>
        </div>

        <div className="lg:col-span-8">
          {/* Members-only offers */}
          {membership?.status === "active" && offers.length > 0 && (
            <div data-testid="member-offers-section" className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <Crown size={16} strokeWidth={1.2} className="text-primary" />
                <h2 className="font-serif text-2xl md:text-3xl text-foreground">
                  <EditableText kEn="account_offers_title_en" kFr="account_offers_title_fr" label="Offers section title"
                    fallback={{ en: "Members-only offers", fr: "Offres réservées aux membres" }} />
                </h2>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {offers.map((o) => (
                  <li key={o.id} data-testid={`member-offer-${o.id}`} className="border border-primary/40 bg-primary/5 p-6">
                    <span className={`inline-block text-[0.6rem] tracking-[0.2em] uppercase px-2.5 py-1 mb-4 ${
                      o.kind === "early_access" ? "bg-primary text-primary-foreground border border-primary" : "border border-primary text-primary"
                    }`}>
                      {o.kind === "early_access"
                        ? (lang === "fr" ? "Accès anticipé" : "Early access")
                        : (lang === "fr" ? "Offre saisonnière" : "Seasonal offer")}
                    </span>
                    <h3 className="font-serif text-xl text-foreground mb-2">{lang === "fr" ? o.title_fr : o.title_en}</h3>
                    {(lang === "fr" ? o.body_fr : o.body_en) && (
                      <p className="text-sm text-muted-foreground leading-relaxed">{lang === "fr" ? o.body_fr : o.body_en}</p>
                    )}
                    {o.valid_until && (
                      <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground mt-4">
                        {lang === "fr" ? "Jusqu'au" : "Until"} {o.valid_until}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">{t.account.myBookings}</h2>
            <Link to="/booking" className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem]">
              {t.nav.bookNow} <ArrowUpRight size={12} strokeWidth={1.2} />
            </Link>
          </div>

          {bookings.length === 0 ? (
            <p data-testid="bookings-empty" className="text-muted-foreground text-base py-12 border-y border-border">
              {t.account.empty}
            </p>
          ) : (
            <ul className="divide-y divide-border border-y border-border">
              {bookings.map((b) => (
                <li key={b.id} data-testid={`my-booking-${b.id}`} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-baseline">
                  <div className="md:col-span-5">
                    <h3 className="font-serif text-xl text-foreground mb-1">{b.service_name}</h3>
                    <p className="text-xs text-muted-foreground">{b.service_price}</p>
                  </div>
                  <div className="md:col-span-4 text-sm text-foreground/85 flex items-center gap-2">
                    <Calendar size={12} strokeWidth={1.2} className="text-primary" />
                    {b.appointment_date} · {b.appointment_time}
                  </div>
                  <div className={`md:col-span-3 md:text-right text-xs tracking-[0.2em] uppercase ${STATUS_COLOR[b.status] || "text-muted-foreground"}`}>
                    {t.account.status[b.status] || b.status}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}
